import * as React from "react"
import { router } from "@inertiajs/react"
import {
  type ColumnDef,
  type ColumnFiltersState,
  type Row,
  type SortingState,
  type VisibilityState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table"
import {
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  SkipBack,
  SkipForward,
} from "lucide-react"

import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { SearchInput } from "@/components/ui/search-input"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Skeleton } from "@/components/ui/skeleton"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TableRowHeader,
} from "@/components/ui/table"
import { tableCardSurface } from "@/components/ui/table-section"
import { useIsMobile } from "@/hooks/use-mobile"
import { t } from "@/lib/i18n"
import { normalizeForSearch } from "@/lib/search"
import { cn } from "@/lib/utils"

import { ColumnHeader, isFilterActive, type FilterValue } from "./column-header"
import { alignmentClass, fieldAlignment, renderField } from "./fields"
import {
  isServerMode,
  type DataColumn,
  type DataTableProps,
  type ServerTableProps,
  type SortDirection,
  type VisitParams,
} from "./types"

export type * from "./types"
export { renderField, fieldAlignment } from "./fields"

const DEFAULT_PER_PAGE_OPTIONS = [ 10, 25, 50, 100 ]

/**
 * How long the box waits after the last keystroke before asking the server.
 *
 * Every index page carried this before the table did, and moving the search
 * into the component without it turned one query into one query per character.
 */
const SEARCH_DEBOUNCE_MS = 350

/**
 * Client-side column filter used by the `searchKey` mode. TanStack's built-in
 * string filter folds case but not accents, which would leave this table as the
 * one search in the product where "taqueria" misses "Taquería" — see
 * app/javascript/lib/search.ts and the matching server-side fold in
 * app/queries/concerns/accent_insensitive_search.rb.
 */
function accentInsensitiveFilter<TData>(row: Row<TData>, columnId: string, filterValue: unknown): boolean {
  return normalizeForSearch(String(row.getValue(columnId) ?? ""))
    .includes(normalizeForSearch(String(filterValue ?? "")))
}

function headerLabel<TData>(column: DataColumn<TData>): string {
  return typeof column.header === "string" ? column.header : String(column.id ?? "")
}

function columnKey<TData>(column: DataColumn<TData>): string {
  return String(
    column.id ?? ("accessorKey" in column ? (column.accessorKey as string) : "")
  )
}

/** Drops what should not appear in the query string at all, so URLs stay short and honest. */
function prune(params: VisitParams): VisitParams {
  return Object.fromEntries(
    Object.entries(params).filter(([ , value ]) => {
      if (value === undefined || value === null || value === "") return false
      if (Array.isArray(value)) return value.length > 0

      return true
    })
  )
}

function serverVisitParams<TData>(
  server: ServerTableProps<TData>,
  overrides: VisitParams
): VisitParams {
  const base: VisitParams = {
    page: server.pagination.page,
    per_page: server.pagination.perPage,
    search: server.search,
    sort: server.sort.column ?? undefined,
    direction: server.sort.column ? server.sort.direction : undefined,
    ...server.filters,
  }

  return prune({ ...base, ...overrides })
}

export function DataTable<TData>(props: DataTableProps<TData>) {
  const {
    columns,
    data,
    timeZone,
    surface = "plain",
    emptyMessage,
    renderMobileCard,
    rowTestId,
    perPageOptions = DEFAULT_PER_PAGE_OPTIONS,
  } = props

  const server = isServerMode(props) ? props : undefined
  const client = isServerMode(props) ? undefined : props

  // One tree or the other, never both. Rendering the table and the card feed
  // together and hiding one with CSS puts every row in the DOM twice, and the
  // old component only got away with it because the feed was opt-in.
  const isMobile = useIsMobile()

  const [ loading, setLoading ] = React.useState(false)
  const [ sorting, setSorting ] = React.useState<SortingState>([])
  const [ columnFilters, setColumnFilters ] = React.useState<ColumnFiltersState>([])
  const [ columnVisibility, setColumnVisibility ] = React.useState<VisibilityState>({})

  const visit = React.useCallback(
    (overrides: VisitParams) => {
      if (!server) return

      router.get(server.url, serverVisitParams(server, overrides), {
        preserveState: true,
        preserveScroll: true,
        replace: true,
        only: server.reloadOnly,
        onStart: () => setLoading(true),
        onFinish: () => setLoading(false),
      })
    },
    [ server ]
  )

  // `visit` closes over `props`, whose identity changes on every render, so an
  // effect that depended on it would clear and re-arm its timer forever and the
  // debounced search would never fire. The ref keeps the callback current while
  // the effect below depends only on values.
  const visitRef = React.useRef(visit)
  visitRef.current = visit

  const isServer = Boolean(server)
  const serverSearch = server?.search ?? ""
  const [ searchDraft, setSearchDraft ] = React.useState(serverSearch)

  // Re-sync when the server's term changes underneath the box — the back
  // button, a link that carries its own query, a full reload.
  React.useEffect(() => {
    setSearchDraft(serverSearch)
  }, [ serverSearch ])

  React.useEffect(() => {
    if (!isServer || searchDraft === serverSearch) return

    const timeout = setTimeout(() => {
      visitRef.current({ search: searchDraft || undefined, page: 1 })
    }, SEARCH_DEBOUNCE_MS)

    return () => clearTimeout(timeout)
  }, [ isServer, searchDraft, serverSearch ])

  // Ascending, then descending, then back to whatever order the query defends
  // by default. Clearing matters because the default is usually the useful one
  // — newest first on an audit log — and without a third state an operator who
  // sorted once can never get it back without reloading the page.
  const toggleServerSort = React.useCallback(
    (sortKey: string, current: SortDirection | null) => {
      if (current === null) return visit({ sort: sortKey, direction: "asc", page: 1 })
      if (current === "asc") return visit({ sort: sortKey, direction: "desc", page: 1 })

      return visit({ sort: undefined, direction: undefined, page: 1 })
    },
    [ visit ]
  )

  const tableColumns = React.useMemo<ColumnDef<TData, unknown>[]>(() => {
    return columns.map((column) => {
      const key = columnKey(column)
      const label = headerLabel(column)
      const alignment = fieldAlignment(column.field)
      const isTitle = column.field?.type === "title"
      const filterKey = column.filterKey ?? key

      const serverSortDirection: SortDirection | null =
        server && server.sort.column === column.sortKey ? server.sort.direction : null

      const clientSortDirection = (): SortDirection | null => {
        const entry = sorting.find((state) => state.id === key)
        if (!entry) return null

        return entry.desc ? "desc" : "asc"
      }

      const filterValue: FilterValue | undefined = server
        ? server.filters?.[filterKey]
        : (columnFilters.find((entry) => entry.id === key)?.value as FilterValue | undefined)

      return {
        ...column,
        id: key,
        // The column that names the row can never be hidden. Hiding it leaves a
        // table of anonymous figures, which is not a view anybody wants.
        enableHiding: isTitle ? false : column.enableHiding,
        filterFn: column.filter?.kind === "enum" ? "arrIncludesSome" : accentInsensitiveFilter,
        header: () => (
          <ColumnHeader
            label={label}
            alignment={alignment}
            sort={
              server
                ? column.sortKey
                  ? {
                      direction: serverSortDirection,
                      onToggle: () => toggleServerSort(column.sortKey as string, serverSortDirection),
                    }
                  : undefined
                : column.enableSorting === false
                  ? undefined
                  : {
                      direction: clientSortDirection(),
                      onToggle: () =>
                        setSorting((current) => {
                          const entry = current.find((state) => state.id === key)
                          if (!entry) return [ { id: key, desc: false } ]
                          if (!entry.desc) return [ { id: key, desc: true } ]

                          return []
                        }),
                    }
            }
            filter={
              column.filter
                ? {
                    definition: column.filter,
                    value: filterValue,
                    onChange: (next) => {
                      if (server) return visit({ [filterKey]: next ?? undefined, page: 1 })

                      setColumnFilters((current) => {
                        const others = current.filter((entry) => entry.id !== key)
                        return next ? [ ...others, { id: key, value: next } ] : others
                      })
                    },
                  }
                : undefined
            }
          />
        ),
        cell: column.cell
          ? column.cell
          : column.field
            ? ({ row, getValue }) => renderField(column.field!, getValue(), row.original, { timeZone })
            : undefined,
      } as ColumnDef<TData, unknown>
    })
  }, [ columns, server, sorting, columnFilters, timeZone, toggleServerSort, visit ])

  const table = useReactTable({
    data,
    columns: tableColumns,
    getCoreRowModel: getCoreRowModel(),
    // In server mode the table holds one page of rows. Running a client row
    // model over them would sort, filter and paginate that page and present the
    // result as though it had done so to the whole table.
    getSortedRowModel: server ? undefined : getSortedRowModel(),
    getFilteredRowModel: server ? undefined : getFilteredRowModel(),
    getPaginationRowModel: server ? undefined : getPaginationRowModel(),
    manualSorting: Boolean(server),
    manualFiltering: Boolean(server),
    manualPagination: Boolean(server),
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onColumnVisibilityChange: setColumnVisibility,
    state: { sorting, columnFilters, columnVisibility },
  })

  const rows = table.getRowModel().rows
  const titleColumn = columns.find((column) => column.field?.type === "title")
  const isCard = surface === "card"
  const searchPlaceholder =
    props.searchPlaceholder ?? t("ui.react.components.ui.data_table.search")
  const noResults = emptyMessage ?? t("ui.react.components.ui.data_table.no_results")

  const searchValue = server
    ? searchDraft
    : ((client?.searchKey && (table.getColumn(client.searchKey)?.getFilterValue() as string)) ?? "")

  const onSearch = (value: string) => {
    // Server mode types into local state and lets the effect above decide when
    // to ask; binding the field straight to the server's value would show the
    // previous answer between keystrokes, which reads as dropped characters.
    if (server) return setSearchDraft(value)
    if (client?.searchKey) table.getColumn(client.searchKey)?.setFilterValue(value)
  }

  const showSearch = Boolean(server) || Boolean(client?.searchKey)

  return (
    <div className="w-full">
      <div className={isCard ? tableCardSurface : undefined}>
        <div
          className={cn(
            "flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between",
            isCard ? "border-b p-4" : "pb-4"
          )}
        >
          {showSearch ? (
            <SearchInput
              containerClassName="w-full sm:max-w-sm"
              placeholder={searchPlaceholder}
              value={searchValue}
              onChange={(event) => onSearch(event.target.value)}
              onClear={() => onSearch("")}
              clearLabel={t("ui.react.components.ui.data_table.clear_search")}
            />
          ) : (
            <span />
          )}
          <div className="ml-auto flex items-center gap-2">
            <ActiveFilterCount columns={columns} server={server} filters={columnFilters} />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline">
                  {t("ui.react.components.ui.data_table.columns")}
                  <ChevronDown className="ml-2 size-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {table
                  .getAllColumns()
                  .filter((column) => column.getCanHide())
                  .map((column) => {
                    const source = columns.find((entry) => columnKey(entry) === column.id)

                    return (
                      <DropdownMenuCheckboxItem
                        key={column.id}
                        checked={column.getIsVisible()}
                        onCheckedChange={(value) => column.toggleVisibility(Boolean(value))}
                      >
                        {source ? headerLabel(source) : column.id}
                      </DropdownMenuCheckboxItem>
                    )
                  })}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        {server?.error ? (
          <div
            role="alert"
            className="text-destructive border-destructive/40 bg-destructive/5 rounded-md border p-4 text-sm"
          >
            {server.error}
          </div>
        ) : null}

        {isMobile ? null : (
        <div className={cn(isCard ? undefined : "overflow-hidden rounded-md border")}>
          <Table>
            <TableHeader>
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <TableHead key={header.id}>
                      {header.isPlaceholder
                        ? null
                        : flexRender(header.column.columnDef.header, header.getContext())}
                    </TableHead>
                  ))}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {loading ? (
                <LoadingRows columns={table.getVisibleFlatColumns().length} rows={data.length || 5} />
              ) : rows.length ? (
                rows.map((row) => (
                  <TableRow key={row.id} data-testid={rowTestId?.(row.original)}>
                    {row.getVisibleCells().map((cell) => {
                      const source = columns.find((entry) => columnKey(entry) === cell.column.id)
                      const alignment = fieldAlignment(source?.field)
                      const content = flexRender(cell.column.columnDef.cell, cell.getContext())

                      return source?.field?.type === "title" ? (
                        <TableRowHeader key={cell.id}>{content}</TableRowHeader>
                      ) : (
                        <TableCell key={cell.id} className={alignmentClass(alignment)}>
                          {content}
                        </TableCell>
                      )
                    })}
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={table.getVisibleFlatColumns().length} className="h-24 text-center">
                    {noResults}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
        )}

        {isMobile ? (
        <div className="divide-y">
          {loading ? (
            <div className="space-y-2 p-4">
              <Skeleton className="h-4 w-1/2" />
              <Skeleton className="h-4 w-1/3" />
            </div>
          ) : rows.length ? (
            rows.map((row) => (
              <React.Fragment key={row.id}>
                {renderMobileCard ? (
                  renderMobileCard(row.original)
                ) : (
                  <MobileRow
                    row={row.original}
                    columns={columns}
                    titleColumn={titleColumn}
                    timeZone={timeZone}
                    testId={rowTestId?.(row.original)}
                  />
                )}
              </React.Fragment>
            ))
          ) : (
            <div className="text-muted-foreground p-6 text-center text-sm">{noResults}</div>
          )}
        </div>
        ) : null}

        <div
          className={cn(
            "flex flex-wrap items-center justify-end gap-2",
            isCard ? "border-t p-4" : "pt-4"
          )}
        >
          <div className="text-muted-foreground flex-1 text-sm whitespace-nowrap">
            {server
              ? t("ui.react.components.ui.data_table.page_of", {
                  page: server.pagination.page,
                  total: server.pagination.totalPages,
                })
              : ""}
          </div>
          {server ? (
            <ServerPager server={server} perPageOptions={perPageOptions} visit={visit} />
          ) : (
            <>
              <Button
                variant="outline"
                size="sm"
                onClick={() => table.previousPage()}
                disabled={!table.getCanPreviousPage()}
              >
                {t("ui.react.components.ui.data_table.previous")}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => table.nextPage()}
                disabled={!table.getCanNextPage()}
              >
                {t("ui.react.components.ui.data_table.next")}
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

function LoadingRows({ columns, rows }: { columns: number; rows: number }) {
  return (
    <>
      {Array.from({ length: Math.min(rows, 10) }, (_, index) => (
        <TableRow key={index}>
          {Array.from({ length: columns }, (__, cell) => (
            <TableCell key={cell}>
              <Skeleton className="h-4 w-full" />
            </TableCell>
          ))}
        </TableRow>
      ))}
    </>
  )
}

/**
 * The mobile feed, generated from the column types rather than hand-written per
 * screen. The `title` column is the headline and needs no `mobile` slot to say
 * so; `secondary` sits beside it, `meta` reads underneath, `hidden` and
 * `actions` stay out.
 */
function MobileRow<TData>({
  row,
  columns,
  titleColumn,
  timeZone,
  testId,
}: {
  row: TData
  columns: DataColumn<TData>[]
  titleColumn?: DataColumn<TData>
  timeZone?: string
  testId?: string
}) {
  // Through the column's own accessor when it has one, so a nested value like
  // `location.name` reads the same here as it does in the table.
  const read = (column: DataColumn<TData>) =>
    "accessorFn" in column && column.accessorFn
      ? column.accessorFn(row, 0)
      : (row as Record<string, unknown>)[columnKey(column)]

  const secondary = columns.filter((column) => column.mobile === "secondary")
  const meta = columns.filter((column) => column.mobile === "meta")
  const actions = columns.find((column) => column.field?.type === "actions")

  return (
    <div className="space-y-2 p-4" data-testid={testId}>
      <div className="flex items-baseline justify-between gap-2">
        {titleColumn?.field ? (
          <span className="min-w-0 truncate">
            {renderField(titleColumn.field, read(titleColumn), row, { timeZone })}
          </span>
        ) : null}
        {secondary.map((column) => (
          <span key={columnKey(column)} className="shrink-0">
            {column.field ? renderField(column.field, read(column), row, { timeZone }) : null}
          </span>
        ))}
      </div>
      {meta.length ? (
        <div className="text-muted-foreground flex flex-wrap items-center gap-2 text-xs">
          {meta.map((column) => (
            <span key={columnKey(column)}>
              {column.field ? renderField(column.field, read(column), row, { timeZone }) : null}
            </span>
          ))}
        </div>
      ) : null}
      {actions?.field ? renderField(actions.field, null, row, { timeZone }) : null}
    </div>
  )
}

function ActiveFilterCount<TData>({
  columns,
  server,
  filters,
}: {
  columns: DataColumn<TData>[]
  server?: ServerTableProps<TData>
  filters: ColumnFiltersState
}) {
  const count = columns.filter((column) => {
    if (!column.filter) return false
    const key = column.filterKey ?? columnKey(column)

    return server
      ? isFilterActive(server.filters?.[key])
      : isFilterActive(filters.find((entry) => entry.id === columnKey(column))?.value as FilterValue)
  }).length

  if (!count) return null

  return (
    <span className="text-muted-foreground text-sm tabular-nums">
      {t("ui.react.components.ui.data_table.filters")}: {count}
    </span>
  )
}

function ServerPager<TData>({
  server,
  perPageOptions,
  visit,
}: {
  server: ServerTableProps<TData>
  perPageOptions: number[]
  visit: (overrides: VisitParams) => void
}) {
  const { page, perPage, totalPages, hasPrevious, hasNext } = server.pagination

  return (
    <>
      <div className="mr-2 flex items-center gap-2">
        <span className="text-muted-foreground text-sm">
          {t("ui.react.components.ui.data_table.page")}
        </span>
        <Select
          value={String(perPage)}
          onValueChange={(value) => visit({ per_page: Number(value), page: 1 })}
        >
          <SelectTrigger
            size="sm"
            className="w-20"
            aria-label={t("ui.react.components.ui.data_table.results_page")}
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {perPageOptions.map((option) => (
              <SelectItem key={option} value={String(option)}>
                {option}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <Button
        variant="outline"
        size="icon-sm"
        aria-label={t("ui.react.components.ui.data_table.go_first_page")}
        onClick={() => visit({ page: 1 })}
        disabled={!hasPrevious}
      >
        <ChevronsLeft className="size-4" />
      </Button>
      <Button
        variant="outline"
        size="icon-sm"
        className="hidden sm:inline-flex"
        aria-label={t("ui.react.components.ui.data_table.back_pages")}
        onClick={() => visit({ page: Math.max(1, page - 5) })}
        disabled={page <= 1}
      >
        <SkipBack className="size-4" />
      </Button>
      <Button
        variant="outline"
        size="icon-sm"
        aria-label={t("ui.react.components.ui.data_table.go_page_anterior")}
        onClick={() => visit({ page: page - 1 })}
        disabled={!hasPrevious}
      >
        <ChevronLeft className="size-4" />
      </Button>
      <Button
        variant="outline"
        size="icon-sm"
        aria-label={t("ui.react.components.ui.data_table.go_page_next")}
        onClick={() => visit({ page: page + 1 })}
        disabled={!hasNext}
      >
        <ChevronRight className="size-4" />
      </Button>
      <Button
        variant="outline"
        size="icon-sm"
        className="hidden sm:inline-flex"
        aria-label={t("ui.react.components.ui.data_table.forward_pages")}
        onClick={() => visit({ page: Math.min(totalPages, page + 5) })}
        disabled={page >= totalPages}
      >
        <SkipForward className="size-4" />
      </Button>
      <Button
        variant="outline"
        size="icon-sm"
        aria-label={t("ui.react.components.ui.data_table.go_last_page")}
        onClick={() => visit({ page: totalPages })}
        disabled={!hasNext}
      >
        <ChevronsRight className="size-4" />
      </Button>
    </>
  )
}
